import {isPlatformServer} from '@angular/common';
import {effect, inject, PLATFORM_ID, signal, Signal} from '@angular/core';
import {EmptyFeatureResult, SignalStoreFeature} from '@ngrx/signals';

import {currentActionNames} from './shared/devtools.info';

const storeRegistry = signal<Record<string, Signal<unknown>>>({});
let synchronizationInitialized = false;

type Action = {type: string};
type ConnectResponse = {
	send: (action: Action, state: Record<string, unknown>) => void;
};
let connection: ConnectResponse | undefined;

function initSynchronization() {
	const initializedStores = new Set<string>();
	effect(() => {
		if (!connection) {
			return;
		}

		let defaultActionName = 'Store Update';
		const stores = storeRegistry();
		const rootState: Record<string, unknown> = {};
		for (const name in stores) {
			const store = stores[name];
			rootState[name] = store();
			if (!initializedStores.has(name)) {
				defaultActionName = 'Initial State';
				initializedStores.add(defaultActionName);
			}
		}

		const names = Array.from(currentActionNames.value);
		const type = names.length ? names.join(', ') : defaultActionName;
		currentActionNames.value = new Set<string>();

		connection.send({type}, rootState);
	});
}

function getValueFromSymbol(obj: unknown, symbol: symbol) {
	if (typeof obj === 'object' && obj && symbol in obj) {
		return (obj as {[key: symbol]: any})[symbol];
	}
}

function getStoreSignal(store: unknown): Signal<unknown> {
	const [signalStateKey] = Object.getOwnPropertySymbols(store);
	if (!signalStateKey) {
		throw new Error('Cannot find State Signal');
	}

	return getValueFromSymbol(store, signalStateKey);
}

type WithDevtoolsOutput = EmptyFeatureResult & {};

/**
 * @param name store's name as it should appear in the DevTools
 */
export function withDevtools<Input extends EmptyFeatureResult>(name: string): SignalStoreFeature<Input, WithDevtoolsOutput> {
	return store => {
		const isServer = isPlatformServer(inject(PLATFORM_ID));
		if (isServer) {
			return store;
		}

		const extensions = window.__REDUX_DEVTOOLS_EXTENSION__;
		if (!extensions) {
			return store;
		}

		if (!connection) {
			connection = extensions.connect({
				name: 'Signal Store - DocCycle',
			});
		}

		const storeSignal = getStoreSignal(store);
		storeRegistry.update(value => ({
			...value,
			[name]: storeSignal,
		}));

		if (!synchronizationInitialized) {
			initSynchronization();
			synchronizationInitialized = true;
		}

		return store;
	};
}
